import { graphql } from 'react-relay';
import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { groupMeetings } from './groupMeetings.ts';
import { mapLoomMeeting } from './mapLoomMeeting.ts';
import type {
	Meeting,
	MeetingDateRange,
	MeetingsEligibility,
	UseMeetingsResult,
} from './meetingTypes.ts';
import type {
	useMeetingsQuery as useMeetingsQuery$data,
	useMeetingsQueryVariables,
} from './__types__/useMeetingsQuery.ts';

/**
 * Live Relay-backed implementation of the Meetings data hook.
 *
 * This is the single seam between the UI and the data layer — every consuming
 * component depends only on the `UseMeetingsResult` contract, never on the query.
 *
 * Data sources (see tech spec https://hello.atlassian.net/wiki/spaces/Growth/pages/7345509583):
 * - `loom_meetingsSearch(id: siteAri)` → the user's recorded (notetaker) meetings.
 *   Returns `{ single, recurring }` with NO date-range argument (30-day server
 *   default), so we client-filter to the requested range (default: last 7 days).
 *   NOTE: swap to a range-capable query when the Loom team ships one.
 * - `loom_settings(siteId)` → drives eligibility (TWC / connect-calendar / show).
 *
 * Out of scope here: action items (recap + one-click actions) — rendered by the
 * swappable `ActionItem` boundary and left empty until the one-click-actions AGG
 * query is available.
 */

const MEETINGS_QUERY = graphql`
	query useMeetingsQuery($siteAri: ID!, $cloudId: ID!, $cloudIds: [ID!]!) {
		tenantContexts(cloudIds: $cloudIds) {
			confluenceEntitlementInfo: entitlementInfo(hamsProductKey: "confluence.ondemand") {
				... on CommerceEntitlementInfoCcp {
					entitlement {
						... on CcpEntitlement {
							relatesFromEntitlements {
								relationshipType
							}
							offering {
								pricingType
							}
						}
					}
				}
			}
			jiraEntitlementInfo: entitlementInfo(hamsProductKey: "jira-software.ondemand") {
				... on CommerceEntitlementInfoCcp {
					entitlement {
						... on CcpEntitlement {
							offering {
								pricingType
							}
						}
					}
				}
			}
		}
		loom_settings(siteId: $cloudId) {
			meetingRecordingsAvailable
			meetingNotesAvailable
			meetingNotesEligible
		}
		loom_connectedCalendars(siteId: $cloudId) {
			guid
		}
		loom_meetingsSearch(id: $siteAri) {
			single {
				id
				title
				startsAt
				recorderId
				recorder {
					accountId
					name
					picture
				}
				inviteeAris
				invitees {
					accountId
					name
					picture
				}
				video {
					id
					url
					isMeeting
					meetingAri
				}
			}
			recurring {
				id
				title
				startsAt
				recorderId
				recorder {
					accountId
					name
					picture
				}
				inviteeAris
				invitees {
					accountId
					name
					picture
				}
				video {
					id
					url
					isMeeting
					meetingAri
				}
			}
		}
	}
`;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export type UseMeetingsOptions = {
	/** Date range to fetch meetings for. Defaults to the last 7 days. */
	dateRange?: MeetingDateRange;
	/** Whether the Meetings gate is on. When false the query is skipped. Defaults to true. */
	enabled?: boolean;
};

/** Build the Loom site ARI used by `loom_meetingsSearch`. */
const toSiteAri = (cloudId: string): string => `ari:cloud:loom::site/${cloudId}`;

/**
 * Whether the site is a TWC customer, per the sanctioned Confluence pattern:
 * a Confluence entitlement that "relates from" a COLLECTION (bundle) relationship
 * indicates the site is part of a TWC collection. Mirrors
 * `useNthUserCrossjoinOnboardingEligible`.
 */
export const deriveHasTwc = (
	tenantContext: NonNullable<useMeetingsQuery$data['tenantContexts']>[number] | undefined,
): boolean => {
	const relatesFromEntitlements =
		tenantContext?.confluenceEntitlementInfo?.entitlement?.relatesFromEntitlements ?? [];
	return relatesFromEntitlements.some(
		(relationship) => relationship?.relationshipType === 'COLLECTION',
	);
};

export type ProductEntitlement = {
	entitlement?: { offering?: { pricingType?: string | null } | null } | null;
} | null;

/** Whether a product entitlement exists (site has the product provisioned). */
export const hasProduct = (info: ProductEntitlement | undefined): boolean =>
	info?.entitlement != null;

/** Whether a product entitlement is a paid plan (excludes FREE / LIMITED_FREE). */
export const isPaid = (info: ProductEntitlement | undefined): boolean =>
	info?.entitlement?.offering?.pricingType === 'PAID';

/**
 * Derive the eligibility state per the experiment targeting spec:
 *
 * 1. Co-use (TWC **paid**) — gated on Loom's `loom_settings` recipe:
 *      - `eligible` — `meetingRecordingsAvailable` AND `meetingNotesAvailable`
 *        (the latter already implies a connected calendar).
 *      - `noConnectedCalendar` — `meetingRecordingsAvailable` AND
 *        `meetingNotesEligible` AND no calendar connected yet (connect-calendar CTA).
 *      - `excluded` — anything else, so we never show a dead-end CTA.
 * 2. `excluded` — site has TWC but is **not** paid (TWC-free). These sites are
 *    out of the experiment entirely: no pill, no banner.
 * 3. `noTwc` (cross-flow) — site has **no** TWC at all but is a paid Atlassian
 *    customer we want to cross-flow into TWC: Confluence paid OR Jira paid.
 *    (Confluence is always present since this surface renders inside Confluence.)
 * 4. `excluded` — everything else (no TWC, neither Confluence nor Jira paid).
 */
export const deriveEligibility = (
	data: useMeetingsQuery$data | undefined,
): Extract<MeetingsEligibility, 'eligible' | 'noTwc' | 'noConnectedCalendar' | 'excluded'> => {
	const tenantContext = data?.tenantContexts?.[0];
	const confluence = tenantContext?.confluenceEntitlementInfo;

	// Site has TWC (any plan). Co-use requires it to be paid; TWC-free is excluded.
	if (deriveHasTwc(tenantContext)) {
		if (!isPaid(confluence)) {
			return 'excluded';
		}

		// Loom AI meeting-notes eligibility, per the Loom team's loom_settings recipe:
		// - `eligible` (show meetings): recordings available AND meeting notes available
		//   (meetingNotesAvailable already implies a connected calendar).
		// - `noConnectedCalendar` (connect-calendar CTA): recordings available AND the
		//   user is eligible for meeting notes but has no calendar connected yet.
		// - anything else falls through to `excluded` so we never show a dead-end CTA.
		const settings = data?.loom_settings;
		const recordingsAvailable = settings?.meetingRecordingsAvailable === true;
		const notesAvailable = settings?.meetingNotesAvailable === true;
		const notesEligible = settings?.meetingNotesEligible === true;
		const hasConnectedCalendar = (data?.loom_connectedCalendars ?? []).some(Boolean);

		if (recordingsAvailable && notesAvailable) {
			return 'eligible';
		}
		if (recordingsAvailable && notesEligible && !hasConnectedCalendar) {
			return 'noConnectedCalendar';
		}
		return 'excluded';
	}

	// No TWC: cross-flow only for paid Atlassian customers.
	return isCrossFlowEligible(data) ? 'noTwc' : 'excluded';
};

/**
 * Whether a non-TWC site qualifies for the cross-flow experience: Confluence paid
 * OR Jira paid. Confluence is always present on this surface. Exported for
 * targeting/analytics use alongside the derived eligibility state.
 */
export const isCrossFlowEligible = (data: useMeetingsQuery$data | undefined): boolean => {
	const tenantContext = data?.tenantContexts?.[0];

	if (deriveHasTwc(tenantContext)) {
		return false;
	}

	return (
		isPaid(tenantContext?.confluenceEntitlementInfo) || isPaid(tenantContext?.jiraEntitlementInfo)
	);
};

// Gate off → treat as not eligible; still fetching → loading; else derive.
const deriveEligibilityState = (
	enabled: boolean,
	loading: boolean,
	data: useMeetingsQuery$data | undefined,
): MeetingsEligibility => {
	if (!enabled) {
		return 'excluded';
	}
	if (loading) {
		return 'loading';
	}
	return deriveEligibility(data);
};

/** Whether an eligibility state is one where the pill is allowed to show. */
const isPillVisibleState = (eligibility: MeetingsEligibility): boolean =>
	eligibility === 'eligible' || eligibility === 'noTwc' || eligibility === 'noConnectedCalendar';

/**
 * Shared query + eligibility derivation for the Meetings surface.
 *
 * This is the single place the `useMeetingsQuery` is issued. Both the
 * feed-level visibility hook and the panel-level data hook call this; because
 * the query uses identical variables + `cache-first`, only ONE network request
 * is made (the second call is a cache hit). It deliberately does NOT read the
 * experiment — exposure is owned solely by `useMeetingsVisibility`.
 */
const useMeetingsQueryData = (
	enabled: boolean,
): {
	data: useMeetingsQuery$data | undefined;
	loading: boolean;
	error?: Error;
	eligibility: MeetingsEligibility;
} => {
	const { cloudId } = useSessionData();

	// Stable reference so the query isn't re-executed on every render.
	const variables = useMemo(
		() => ({ siteAri: toSiteAri(cloudId), cloudId, cloudIds: [cloudId] }),
		[cloudId],
	);

	const { data, loading, error } = useQueryInterop<
		useMeetingsQuery$data,
		useMeetingsQueryVariables
	>(MEETINGS_QUERY, {
		variables,
		errorPolicy: 'all',
		fetchPolicy: 'cache-first',
		// Gate off → skip the query (no request on the feed critical path).
		skip: !enabled,
	});

	// Gate off → never fetching, so not "loading".
	const eligibility: MeetingsEligibility = deriveEligibilityState(enabled, loading, data);

	return { data, loading, error: error ?? undefined, eligibility };
};

/**
 * Feed-level visibility hook: gate → targeting → flag.
 *
 * Owns the SINGLE experiment exposure. Called once from `Feed` to decide whether
 * to render the Meetings pill. Exposure fires for every targeted user (control
 * AND treatment) so the experiment has both cohorts; `shouldShow` is true only
 * for treatment, so only treatment users actually see the pill.
 */
export const useMeetingsVisibility = (
	options: { enabled?: boolean } = {},
): { shouldShow: boolean } => {
	const { enabled = true } = options;
	const { eligibility } = useMeetingsQueryData(enabled);

	// Read the experiment only once targeting passes, so exposure fires for the
	// targeted cohort only. `excluded`/`loading` never expose and never show.
	const shouldShow =
		isPillVisibleState(eligibility) &&
		expValEquals('meetings_tab_in_confluence_for_you_page', 'cohort', 'variant');

	return { shouldShow };
};

/**
 * Panel-level data hook: meetings list + grouping for the rendered panel.
 *
 * Consumes the SAME cached query as `useMeetingsVisibility` (no extra network
 * request) and does NOT read the experiment (no duplicate exposure). Only
 * mounted for treatment users, since the feed gates the pill on `shouldShow`.
 */
export const useMeetings = (options: UseMeetingsOptions = {}): UseMeetingsResult => {
	const { dateRange, enabled = true } = options;
	const { userId } = useSessionData();

	const rangeStart = dateRange?.rangeStart;
	const rangeEnd = dateRange?.rangeEnd;

	const { data, loading, error, eligibility } = useMeetingsQueryData(enabled);

	const groups = useMemo(() => {
		if (loading || eligibility !== 'eligible' || !userId) {
			return [];
		}

		const search = data?.loom_meetingsSearch;
		const rawMeetings = [...(search?.single ?? []), ...(search?.recurring ?? [])];

		// Interim date window: use the caller's range, else the last 7 days.
		const lowerBound = rangeStart ? Date.parse(rangeStart) : Date.now() - SEVEN_DAYS_MS;
		const upperBound = rangeEnd ? Date.parse(rangeEnd) : Date.now();

		// Single pass: keep only meetings the user recorded or was invited to
		// (attended), that have a playable recording, and that fall in range.
		const meetings: Meeting[] = [];
		for (const m of rawMeetings) {
			if (!m) {
				continue;
			}
			const isParticipant = m.recorderId === userId || (m.inviteeAris ?? []).includes(userId);
			if (!isParticipant || !m.video?.url) {
				continue;
			}
			const mapped = mapLoomMeeting(m);
			const t = Date.parse(mapped.startsAt);
			if (Number.isNaN(t) || t < lowerBound || t > upperBound) {
				continue;
			}
			meetings.push(mapped);
		}
		meetings.sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));

		return groupMeetings(meetings);
	}, [loading, eligibility, userId, data, rangeStart, rangeEnd]);

	const totalCount = useMemo(
		() => groups.reduce((sum, group) => sum + group.meetings.length, 0),
		[groups],
	);

	return {
		eligibility,
		loading,
		error,
		groups,
		totalCount,
		// Visibility/exposure is owned by `useMeetingsVisibility` at the feed
		// level; the panel is only mounted for treatment users. Kept in the
		// contract for back-compat; the panel does not gate on it.
		shouldShow: isPillVisibleState(eligibility),
	};
};
